import { useState, useEffect, useCallback } from "react";
import { API_BASE, fetchWithAuth } from "../utils/api";

export function useSheets(user) {
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const normalizedRole = user?.role?.toLowerCase();
  const canSwitchMonth = ['admin', 'dispatchers', 'accounting'].includes(normalizedRole);

  const loadSheets = useCallback(async () => {
    if (!canSwitchMonth) {
      setSheets([]);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await fetchWithAuth(`${API_BASE}/sheets/list`);
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Không thể tải danh sách tháng");
      }
      setSheets(data.sheets || []);
      
      // Tìm sheet đang active
      const active = data.sheets?.find(s => s.is_active === 1) || 
                     data.sheets?.find(s => s.is_default === 1) ||
                     data.sheets?.[0];
      if (active) {
        setActiveSheet(active);
      }
    } catch (err) {
      setError(err.message || "Có lỗi xảy ra");
    } finally {
      setLoading(false);
    }
  }, [canSwitchMonth]);

  const switchSheet = useCallback(async (sheetId) => {
    if (!canSwitchMonth) return;

    // Cập nhật activeSheet ngay lập tức từ danh sách sheets hiện có để UI phản hồi nhanh
    // Tạo một bản copy để đảm bảo React nhận biết thay đổi
    const selectedSheet = sheets.find(s => s.id === sheetId);
    if (selectedSheet) {
      // Tạo object mới để đảm bảo React re-render
      setActiveSheet({ ...selectedSheet });
    }

    setLoading(true);
    setError("");
    try {
      const res = await fetchWithAuth(`${API_BASE}/sheets/switch/${sheetId}`, {
        method: "PUT",
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Không thể chuyển đổi tháng");
      }
      
      // Reload danh sách sheets để cập nhật is_active và đảm bảo activeSheet có đầy đủ thông tin
      const sheetsRes = await fetchWithAuth(`${API_BASE}/sheets/list`);
      const sheetsData = await sheetsRes.json();
      if (sheetsRes.ok && sheetsData.success) {
        const updatedSheets = sheetsData.sheets || [];
        setSheets(updatedSheets);
        // Tìm lại activeSheet từ danh sách mới để có đầy đủ thông tin (bao gồm is_locked)
        const updatedActive = updatedSheets.find(s => s.id === sheetId);
        if (updatedActive) {
          // Tạo object mới để đảm bảo React re-render
          setActiveSheet({ ...updatedActive });
        } else if (data.activeSheet) {
          // Fallback: dùng data từ response
          setActiveSheet({ ...data.activeSheet });
        }
      } else if (data.activeSheet) {
        // Fallback: dùng data từ response nếu không load được danh sách
        setActiveSheet({ ...data.activeSheet });
      }
      
      return data.activeSheet;
    } catch (err) {
      setError(err.message || "Có lỗi xảy ra");
      // Nếu có lỗi, khôi phục activeSheet từ danh sách sheets hiện có
      if (sheets.length > 0) {
        const currentActive = sheets.find(s => s.is_active === 1) || 
                             sheets.find(s => s.is_default === 1) ||
                             sheets[0];
        if (currentActive) {
          setActiveSheet({ ...currentActive });
        }
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, [canSwitchMonth, sheets]);

  useEffect(() => {
    if (user && canSwitchMonth) {
      loadSheets();
    }
  }, [user, canSwitchMonth, loadSheets]);

  return {
    sheets,
    activeSheet,
    loading,
    error,
    loadSheets,
    switchSheet,
    canSwitchMonth,
  };
}
